import {
  FlaskConical,
  Brain,
  HeartPulse,
  UtensilsCrossed,
  Stethoscope,
  Activity,
  Dna,
  User,
  BarChart3,
  BookOpen,
  ClipboardList,
  ShieldCheck,
  Package,
  CreditCard,
  Clock,
  type LucideIcon
} from 'lucide-react'

export interface Pillar {
  icon: LucideIcon
  title: string
  description: string
}

export const pillars: Pillar[] = [
  {
    icon: FlaskConical,
    title: 'Farmacogenética de Canabinoides',
    description:
      'Análise de variantes genéticas que influenciam o metabolismo de THC e CBD, sensibilidade ao THC e riscos associados.'
  },
  {
    icon: Brain,
    title: 'Neurosegurança e Susceptibilidade Comportamental',
    description:
      'Avaliação de vulnerabilidade neurocomportamental incluindo sono, humor, sensibilidade à dor e predisposição a dependências.'
  },
  {
    icon: HeartPulse,
    title: 'Predisposição Metabólica e Cardiometabólica',
    description: 'Estratificação de risco para diabetes tipo 2, NAFLD, perfil lipídico genético e hipertensão arterial.'
  },
  {
    icon: UtensilsCrossed,
    title: 'Perfis Genéticos — Obesidade e GLP-1',
    description:
      'Tendência genética à obesidade, resposta a agonistas de GLP-1 e perfil ampliado de comportamento alimentar.'
  }
]

// ─── Como funciona (steps) ───────────────────────────────────────

export interface Step {
  number: string
  title: string
  description: string
}

export const howItWorksSteps: Step[] = [
  {
    number: '01',
    title: 'Coleta domiciliar',
    description:
      'O paciente realiza a coleta de uma amostra biológica em casa com o kit enviado, de forma simples e não invasiva.'
  },
  {
    number: '02',
    title: 'Análise laboratorial',
    description:
      'A amostra é processada pela Bioma Genetics para identificar variações no DNA relacionadas ao metabolismo e perfil clínico.'
  },
  {
    number: '03',
    title: 'Laudo estruturado',
    description:
      'Os dados são integrados em um laudo que classifica riscos, interpreta impactos clínicos e sugere caminhos terapêuticos.'
  }
]

// ─── Tabela comparativa ──────────────────────────────────────────

export interface ComparisonRow {
  feature: string
  basic: boolean
  complete: boolean
}

export interface ComparisonModule {
  module: string
  rows: ComparisonRow[]
}

export const comparisonTable: ComparisonModule[] = [
  {
    module: 'Farmacogenética de Canabinoides',
    rows: [
      { feature: 'Metabolismo de THC (CYP2C9, CYP3A4)', basic: true, complete: true },
      { feature: 'Metabolismo de CBD (CYP2C9, CYP2C19, CYP3A4)', basic: true, complete: true },
      { feature: 'Sensibilidade ao THC', basic: true, complete: true },
      { feature: 'Psicose induzida por cannabis', basic: true, complete: true },
      { feature: 'Dependência por cannabis', basic: true, complete: true }
    ]
  },
  {
    module: 'Metabolismo & Obesidade',
    rows: [
      { feature: 'Tendência genética à obesidade', basic: true, complete: true },
      { feature: 'Resposta a agonistas de GLP-1', basic: true, complete: true },
      { feature: 'Perfil ampliado de comportamento alimentar', basic: false, complete: true }
    ]
  },
  {
    module: 'Risco Cardiometabólico',
    rows: [
      { feature: 'Predisposição a DM2', basic: false, complete: true },
      { feature: 'NAFLD', basic: false, complete: true },
      { feature: 'Perfil lipídico genético', basic: false, complete: true },
      { feature: 'HAS', basic: false, complete: true }
    ]
  },
  {
    module: 'Neurocomportamental Ampliado',
    rows: [
      { feature: 'Sono', basic: false, complete: true },
      { feature: 'Humor e estresse', basic: false, complete: true },
      { feature: 'Sensibilidade à dor', basic: false, complete: true },
      { feature: 'Dependências ampliadas', basic: false, complete: true }
    ]
  },
  {
    module: 'Performance & Estética',
    rows: [
      { feature: 'Aptidão física', basic: false, complete: true },
      { feature: 'Risco de lesões', basic: false, complete: true },
      { feature: 'Saúde cutânea e envelhecimento', basic: false, complete: true },
      { feature: 'Saúde masculina', basic: false, complete: true }
    ]
  }
]

// ─── Para quem serve ─────────────────────────────────────────────

export interface AudienceCard {
  icon: LucideIcon
  title: string
  items: string[]
}

export const audienceCards: AudienceCard[] = [
  {
    icon: Stethoscope,
    title: 'Médicos prescritores de Cannabis',
    items: ['Ajuste fino de dose', 'Prevenção de eventos adversos', 'Estratificação de risco psiquiátrico']
  },
  {
    icon: Brain,
    title: 'Psiquiatras e neurologistas',
    items: ['Avaliação de vulnerabilidade comportamental', 'Suporte à decisão terapêutica']
  },
  {
    icon: Activity,
    title: 'Endocrinologistas',
    items: ['Predisposição a DM2', 'Resposta a GLP-1', 'Obesidade e comportamento alimentar']
  },
  {
    icon: Dna,
    title: 'Clínicos e medicina personalizada',
    items: ['Estratificação cardiometabólica', 'Medicina preventiva']
  },
  {
    icon: User,
    title: 'Pacientes — autoconhecimento',
    items: [
      'Compreensão do próprio perfil genético',
      'Entender predisposições metabólicas',
      'Avaliar riscos associados ao uso de cannabis'
    ]
  }
]

// ─── Como interpretar ────────────────────────────────────────────

export interface InterpretationBlock {
  icon: LucideIcon
  title: string
  items: string[]
}

export const interpretationBlocks: InterpretationBlock[] = [
  {
    icon: BarChart3,
    title: 'Estratificação de Risco',
    items: ['Baixo', 'Intermediário', 'Aumentado']
  },
  {
    icon: BookOpen,
    title: 'Interpretação por módulo',
    items: ['Impacto clínico', 'Implicações terapêuticas', 'Nível de evidência científica']
  },
  {
    icon: ClipboardList,
    title: 'Sugestões de conduta',
    items: ['Diretrizes formais', 'Avaliação clínica individualizada']
  }
]

// ─── Planos / Preços ─────────────────────────────────────────────

export interface PricingPlan {
  name: string
  tag: string
  description: string
  originalPrice: string
  discountedPrice: string
  discountLabel: string
  highlights: { icon: LucideIcon; text: string }[]
  /** Nota de rodapé (ex.: prazo a partir do laboratório) */
  footnote?: string
  featured: boolean
}

/** Exibido junto ao preço promocional no card (fora da lista de benefícios) */
export const pricingLaunchLabel = 'Preço de lançamento'

export const pricingPlans: PricingPlan[] = [
  {
    name: 'CANNECT Code BÁSICO',
    tag: 'Essencial',
    description: 'Foco em farmacogenética + obesidade. Ideal para início de prescrição.',
    originalPrice: 'R$ 749,00',
    discountedPrice: 'R$ 561,75',
    discountLabel: '25% OFF',
    highlights: [
      { icon: CreditCard, text: 'Parcelamento disponível' },
      { icon: Package, text: 'Coleta domiciliar pelo paciente' },
      { icon: Clock, text: 'Resultado em 72 horas úteis*' }
    ],
    footnote: '* A partir do recebimento pelo laboratório.',
    featured: false
  },
  {
    name: 'CANNECT Code COMPLETO',
    tag: 'Mais completo',
    description: 'Avaliação metabólica e neurocomportamental ampliada. Ideal para medicina personalizada integral.',
    originalPrice: 'R$ 1.999,00',
    discountedPrice: 'R$ 1.499,25',
    discountLabel: '25% OFF',
    highlights: [
      { icon: CreditCard, text: 'Parcelamento disponível' },
      { icon: Package, text: 'Coleta domiciliar pelo paciente' },
      { icon: Clock, text: 'Resultado em 20 dias úteis*' }
    ],
    footnote: '* A partir do recebimento pelo laboratório.',
    featured: true
  }
]

// ─── FAQ ─────────────────────────────────────────────────────────

export interface FaqItem {
  question: string
  answer: string
}

// FAQ Pacientes (visível para todos)
export const faqPatientItems: FaqItem[] = [
  {
    question: 'O que é o CANNECT Code?',
    answer:
      'Um teste genético que analisa variações no seu DNA associadas à forma como seu organismo responde a canabinoides, metabolismo, apetite, risco cardiometabólico, comportamento alimentar, sono, humor e outras áreas da saúde. Não é um teste diagnóstico e não substitui consulta médica.'
  },
  {
    question: 'Qual a diferença entre o BÁSICO e o COMPLETO?',
    answer:
      'O BÁSICO cobre metabolismo de CBD/THC, sensibilidade ao THC, risco de dependência por cannabis e obesidade/GLP-1. O COMPLETO inclui tudo do BÁSICO, mais risco cardiometabólico, sono/humor/dor, aptidão física, saúde cutânea e saúde masculina.'
  },
  {
    question: 'O teste diz qual dose de CBD ou THC eu devo usar?',
    answer:
      'O teste não prescreve dose. Ele fornece informações genéticas que ajudam o médico e o dentista a definir dose inicial, velocidade de titulação e monitoramento com mais segurança.'
  },
  {
    question: 'O teste substitui exames laboratoriais?',
    answer: 'Não. Ele complementa exames clínicos e laboratoriais.'
  },
  {
    question: 'Meu resultado pode mudar ao longo da vida?',
    answer: 'Não. O DNA não muda. O que muda é a interpretação científica ao longo do tempo.'
  },
  {
    question: 'O teste detecta doenças?',
    answer:
      'Não. Ele indica predisposições genéticas — que podem ou não se manifestar, dependendo do estilo de vida e ambiente.'
  },
  {
    question: 'Se eu tiver risco genético aumentado para obesidade ou dependência, isso significa que vou desenvolver?',
    answer: 'Não. Genética é predisposição, não destino.'
  },
  {
    question: 'O teste pode prever risco de psicose com cannabis?',
    answer:
      'Ele avalia variantes associadas a maior sensibilidade, mas não diagnostica ou prediz com certeza um transtorno psiquiátrico.'
  },
  {
    question: 'O teste é confidencial?',
    answer:
      'Sim. O CANNECT Code trata dados genéticos como dados pessoais sensíveis, em conformidade com a LGPD (Lei nº 13.709/2018). Os resultados não são compartilhados com planos de saúde, empregadores ou para fins trabalhistas/securitários. Qualquer compartilhamento ocorre apenas mediante autorização do paciente ou obrigação legal.'
  },
  {
    question: 'Como é feita a coleta?',
    answer:
      'Você recebe um kit de autocoleta em casa. Escaneie o QR Code do kit para ativar e preencher o questionário. Faça a coleta seguindo o passo a passo e envie a amostra. Quando concluído, acesse seus resultados online. A ativação do kit é obrigatória — kits não ativados não entram em processamento.'
  },
  {
    question: 'Quem deve fazer o teste?',
    answer:
      'Pacientes que vão iniciar terapia com canabinoides, pessoas com histórico familiar de obesidade ou diabetes, pacientes com dificuldade de resposta terapêutica e pessoas interessadas em medicina personalizada.'
  },
  {
    question: 'Em quanto tempo recebo o resultado?',
    answer:
      'CANNECT Code BÁSICO: até 72 horas úteis após recebimento da amostra. CANNECT Code COMPLETO: até 20 dias úteis. O laudo é disponibilizado em formato digital.'
  },
  {
    question: 'Política de Cancelamento',
    answer:
      'O cancelamento pode ser solicitado em até 7 dias úteis após o recebimento dos kits, desde que a análise laboratorial não tenha sido iniciada. Será cobrado custo de envio e devolução (R$ 50,00). Após 7 dias, o caso é analisado individualmente.'
  },
  {
    question: 'Reenvio de kit (perda, danos ou extravio)',
    answer:
      'Perda ou danos causados pelo cliente: cobrança de R$ 50,00 para reenvio. Extravio durante transporte: reenvio sem cobrança. Danos no recebimento devem ser reportados com fotos em até 48h.'
  },
  {
    question: 'Devolução de kit',
    answer:
      'O kit deve estar intacto, sem uso e lacrado. A devolução pode gerar custos logísticos/administrativos conforme a política de cancelamento. Kits já utilizados não são elegíveis para devolução, exceto em caso de defeito de fabricação.'
  }
]

// FAQ Profissionais de Saúde (visível apenas para PLH — registration_type === 2)
export const faqProfessionalItems: FaqItem[] = [
  {
    question: 'Qual é a finalidade clínica do CANNECT Code?',
    answer:
      'O CANNECT Code é um exame genético de suporte à decisão clínica que integra farmacogenética de canabinoides, neurosegurança e susceptibilidade comportamental, predisposição metabólica e cardiometabólica, e perfis relacionados a obesidade, GLP-1 e comportamento alimentar. Não possui finalidade diagnóstica e não substitui diretrizes clínicas formais.'
  },
  {
    question: 'Qual é a metodologia empregada?',
    answer:
      'O CANNECT Code Básico utiliza genotipagem direcionada por PCR em tempo real (qPCR) com ensaios TaqMan™ SNP Genotyping, analisando 16 SNPs específicos. O CANNECT Code Completo utiliza NGS direcionado com enriquecimento por captura híbrida, com preparação de biblioteca (Twist EF 2.0) e sequenciamento em plataformas Illumina NextSeq 2000 ou MGI DNBSEQ.'
  },
  {
    question: 'O exame utiliza modelo poligênico formal?',
    answer:
      'Algumas categorias (ex: obesidade, risco cardiometabólico) são construídas a partir de múltiplas variantes com evidência científica replicada. A classificação é expressa de forma qualitativa: predisposição reduzida, intermediária ou aumentada. Não substitui avaliação clínica ou exames laboratoriais.'
  },
  {
    question: 'Como o exame pode auxiliar na prescrição de canabinoides?',
    answer:
      'O módulo de farmacogenética permite estimar tendência a metabolismo reduzido ou aumentado de THC/CBD, identificar maior susceptibilidade a efeitos cognitivos sob exposição ao THC e apoiar decisão de titulação mais conservadora quando pertinente. Não define dose.'
  },
  {
    question: 'O teste prediz risco psiquiátrico?',
    answer:
      'Não. Avalia susceptibilidade genética associada a maior sensibilidade ao THC em vias dopaminérgicas e endocanabinoides. Não é ferramenta diagnóstica psiquiátrica.'
  },
  {
    question: 'O módulo de GLP-1 pode orientar prescrição?',
    answer:
      'O exame avalia variantes associadas à resposta diferencial a agonistas de GLP-1. Não substitui critérios clínicos formais de indicação.'
  },
  {
    question: 'Quais são as principais limitações?',
    answer:
      'Não é sequenciamento completo do genoma. Não detecta variantes raras fora do painel. Não substitui avaliação psiquiátrica ou metabólica formal. Resultados expressam predisposição relativa.'
  },
  {
    question: 'Em quais cenários clínicos o exame pode agregar valor?',
    answer:
      'Quando existe dúvida sobre resposta terapêutica, há histórico familiar relevante, o risco comportamental precisa ser ponderado, a estratégia envolve intervenção farmacológica de longo prazo ou o médico deseja estruturar abordagem personalizada com base biológica.'
  },
  {
    question: 'Quais marcadores são analisados no Básico?',
    answer:
      'O Básico analisa 16 SNPs: AKT1 (rs1130233, rs2494732), CNR1 (rs806368, rs1049353), FAAH (rs324420), CYP2C19 (rs4986893), CYP2C9 (rs1057910), CYP3A4 (rs2242480), ARRB1 (rs140226575), GLP1R (rs6923761, rs10305420), COMT (rs4680, rs4633), OPRM1 (rs1799971), FTO (rs9939609, rs8050136).'
  },
  {
    question: 'Política de Cancelamento',
    answer:
      'O cancelamento pode ser solicitado em até 7 dias úteis após o recebimento dos kits, desde que a análise laboratorial não tenha sido iniciada. Será cobrado custo de envio e devolução (R$ 50,00). Após 7 dias, o caso é analisado individualmente, podendo incluir custos laboratoriais.'
  }
]
